/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box } from '@mui/material'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { confirmShipmentFields } from '../../../constants/forms/shipmentFields'
import MovementsShipmentsTransferList from './components/MovementsShipmentsTransferList'
import MovementEnum from '../../../enums/movement-enum'
import Button from '../../../components/Button'
import EquipmentsList from './components/EquipmentsList'
import useForm from '../../../hooks/useForm'
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import ShipmentsDetailsSection from './components/ShipmentsDetailsSection'
import Form from '../../../components/forms/Form'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useSectionFields } from '../../../hooks/useSectionFields'
import { GenericStatusEnum } from '../../../enums/generic-status-enum'
import BaseDestinationSelector from './components/BaseDestinationSelector' // 🆕
import { useReport } from '../../../context/ReportContext'

const ConfirmShipmentsPage = () => {
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { enqueueReports } = useReport()
  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
  } = useBaseService(navigate)
  const location = useLocation()
  const from = location.state?.from || '/aluguel/carregamentos'

  const queryParams = new URLSearchParams(location.search)
  // Vem da página de requisições do contrato
  const movement_type_id = queryParams.get('movement_type_id')
  const recipient_customer_id = queryParams.get('recipient_customer_id')

  const { updateField: updateProps } = useSectionFields()

  // Estados das tabelas
  const [notConfirmedItems, setNotConfirmedItems] = useState([])
  const [confirmedItems, setConfirmedItems] = useState([])
  const [notConfirmedItemsReplacement, setNotConfirmedItemsReplacement] =
    useState([])
  const [confirmedItemsReplacement, setConfirmedItemsReplacement] = useState([])

  // 🆕 Base de destino (devolução) ou base da reserva
  const [destinationBaseId, setDestinationBaseId] = useState(null)

  const [shipmentSection] = confirmShipmentFields

  const [shipmentFieldsState, setshipmentFieldsState] = useState(
    shipmentSection.fields
  )

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([shipmentSection]),
    [shipmentSection]
  )

  // 🆕 Resetar a base quando o tipo de movimento mudar
  useEffect(() => {
    setDestinationBaseId(null)
  }, [formData.shipment.movement_type_id?.value])

  const customerSelect = useRemoteSelectOptions({
    queryKey: ['shipment-confirmation-pending-customers', shipmentId],
    baseServiceGet,
    entityPath: `${entities.shipments.customers(shipmentId)}?pending_only=true`,
    fieldId: 'shipment.recipient_customer_id',
    formData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    immediateFetch: !!shipmentId,
    setFormData,
    perPage: 999999,
    enablePagination: false,
    refetchOnWindowFocus: true,
    withoutDeletedAt: true,
  })

  const movementTypesSelect = useRemoteSelectOptions({
    queryKey: [
      'movementTypes',
      shipmentId,
      customerSelect.selectedValue?.value,
    ],
    baseServiceGet,
    entityPath: entities.shipments.movementTypes(shipmentId),
    fieldId: 'shipment.movement_type_id',
    formData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    enabled: !!shipmentId,
    immediateFetch: !!shipmentId,
    setFormData,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    withoutDeletedAt: true,
  })

  useEffect(() => {
    const selectedCustomerId = customerSelect.selectedValue?.value
    if (
      selectedCustomerId == null ||
      customerSelect.isLoading ||
      customerSelect.isFetching ||
      !customerSelect.rawData
    ) {
      return
    }

    const hasPendingItems = customerSelect.rawData.some(
      (customer) => String(customer.id) === String(selectedCustomerId),
    )

    if (!hasPendingItems) {
      customerSelect.reset()
    }
  }, [
    customerSelect.isLoading,
    customerSelect.isFetching,
    customerSelect.rawData,
    customerSelect.reset,
    customerSelect.selectedValue?.value,
  ])

  const selects = {
    'shipment.recipient_customer_id': customerSelect,
    'shipment.movement_type_id': movementTypesSelect,
  }

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const generateUUID = () => crypto.randomUUID()
  const isReplacement =
    Number(formData.shipment.movement_type_id?.value) ===
    MovementEnum.SUBSTITUICAO

  // --- Query para buscar equipamentos de um carregamento específico (para o modal de confirmação) ---
  const {
    data: itemsForConfirmationModal,
    isFetching: isFetchingShipmentItems,
  } = useQueryWithLoader({
    queryKey: [
      'shipmentEquipments',
      shipmentId,
      movementTypesSelect.selectedValue?.value,
      customerSelect.selectedValue?.value,
    ],
    queryFn: async () => {
      showLoader()

      const filters = {
        deleted_at: false,
      }
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 999999999999,
      }

      const response = await baseServiceGet(
        entities.shipments.items.get(shipmentId),
        params
      )

      const rawItems = response.result?.data ?? []

      const newItems = []
      const replacementItemsMap = new Map()

      const filteredItems = rawItems.filter((item) => {
            const movement =
              item?.movement_item?.movement || item?.volume?.movement
            const movementTypeId =
              item?.movement_item?.movement_type_id ??
              movement?.movement_type?.id ??
              movement?.movement_type_id

            return (
              movement &&
              String(movementTypeId) ===
                String(movementTypesSelect.selectedValue?.value) &&
              item?.status_id === GenericStatusEnum.NOT_ATTENDED &&
              String(movement.recipient_customer_id) ===
                String(customerSelect.selectedValue?.value)
            )
          })

      const movementIds = [
        ...new Set(
          filteredItems
            .map(
              (item) =>
                (item?.movement_item?.movement || item?.volume?.movement)?.id
            )
            .filter((id) => id !== null && id !== undefined)
        ),
      ]

      // ! Substituição
      if (isReplacement) {
        const oldEquipments = []
        const newEquipmentsMap = new Map()

        for (const item of filteredItems) {
          const isEquipment = item?.movement_item?.equipment_id

          if (isEquipment) {
            const oldEquipment = item.movement_item?.order_item?.equipment
            const newEquipmentId = item.movement_item?.equipment_id

            if (oldEquipment && oldEquipment.id !== newEquipmentId) {
              oldEquipments.push({
                uuid: generateUUID(),
                value: oldEquipment?.id,
                label: `${oldEquipment.number} - ${oldEquipment.name}`,
                type: 'equipment',
              })
            }

            const newEquipment = {
              uuid: generateUUID(),
              value: newEquipmentId,
              label: `${item.movement_item?.equipment?.number} - ${item.movement_item?.equipment?.name}`,
              type: 'equipment',
            }

            newEquipmentsMap.set(newEquipment.uuid, newEquipment)
          }
        }

        return {
          movementIds,
          rawItems,
          notConfirmedItems: oldEquipments,
          notConfirmedItemsReplacement: Array.from(newEquipmentsMap.values()),
        }
      } else {
        // ! Aluguel, Reserva, Devolução e Avulso
        for (const item of filteredItems) {
          const isEquipment = item?.movement_item?.equipment_id
          if (isEquipment) {
            const newEquipment = {
              uuid: generateUUID(),
              value: item.movement_item?.equipment_id,
              label: `${item.movement_item?.equipment?.number} - ${item.movement_item?.equipment?.name}`,
              type: 'equipment',
            }

            newItems.push(newEquipment)

            const oldEquipment = item.movement_item?.order_item?.equipment
            const newEquipmentId = item.movement_item?.equipment_id

            if (oldEquipment && oldEquipment?.id !== newEquipmentId) {
              replacementItemsMap.set(oldEquipment?.id, {
                uuid: generateUUID(),
                value: oldEquipment?.id,
                label: `${oldEquipment.number} - ${oldEquipment.name}`,
                type: 'equipment',
              })
            }
          }
        }

        console.log(filteredItems)

        // * Adicionar apenas uma vez o volume de insumos
        // * agrupa por id e nome
        const volumes = filteredItems
          ?.filter((item) => item.volume?.name)
          // * Mostrar volume se tiver pelo menos um insumo (product_item_id).
          // * Mostrar volume se tiver insumos e equipamentos.
          // * Não mostrar volume se tiver apenas equipamentos.
          .filter((item) =>
            item.volume.items.some((volumeItem) => !!volumeItem.product_item_id)
          )
          .reduce((groups, item) => {
            const group = groups.find(
              (g) =>
                g.volume.id === item.volume.id &&
                g.volume.name === item.volume.name
            )

            if (group) {
              group.items.push(item)
            } else {
              groups.push({
                volume: item.volume,
                items: [item],
              })
            }

            return groups
          }, [])

        volumes.forEach((item) => {
          newItems.push({
            uuid: generateUUID(),
            value: item.volume?.id,
            label: `${item.volume?.code} - ${item.volume?.name}`,
            type: 'volume',
          })
        })

        return {
          movementIds,
          rawItems,
          notConfirmedItems: newItems,
          notConfirmedItemsReplacement: Array.from(
            replacementItemsMap.values()
          ),
        }
      }
    },
    enabled:
      !!shipmentId,
    refetchOnWindowFocus: false,
  })

  // * Quando vier da página de movimentos -> carregamentos, preencher os selects
  useEffect(() => {
    if (!movement_type_id || !recipient_customer_id) {
      return
    }

    const movementTypeOption = movementTypesSelect.options.find(
      (option) => option.value === Number(movement_type_id)
    )
    const customerOption = customerSelect.options.find(
      (option) => option.value === Number(recipient_customer_id)
    )

    const hasMatchingPendingItem = itemsForConfirmationModal?.rawItems?.some(
      (item) => {
        const movement = item?.movement_item?.movement || item?.volume?.movement
        const itemMovementTypeId =
          item?.movement_item?.movement_type_id ??
          movement?.movement_type?.id ??
          movement?.movement_type_id

        return (
          movement &&
          item?.status_id === GenericStatusEnum.NOT_ATTENDED &&
          String(itemMovementTypeId) === String(movement_type_id) &&
          String(movement.recipient_customer_id) ===
            String(recipient_customer_id)
        )
      }
    )

    // * Só preenche quando há item pendente para esse tipo e cliente no carregamento.
    if (!movementTypeOption || !customerOption || !hasMatchingPendingItem) {
      return
    }

    setFormData((prev) => ({
      ...prev,
      shipment: {
        ...prev.shipment,
        movement_type_id: {
          value: Number(movement_type_id),
          label: movementTypeOption.label,
        },
        recipient_customer_id: {
          value: Number(recipient_customer_id),
          label: customerOption.label,
        },
      },
    }))

    // * Desabilita os campos para que o usuário só possa confirmar esse carregamento
    let updatedfields = [...confirmShipmentFields[0].fields]

    updatedfields = updateProps({
      arrayDependente: updatedfields,
      field_id: [
        'shipment.recipient_customer_id',
        'shipment.movement_type_id',
      ],
      updates: {
        disabled: true,
      },
    })

    setshipmentFieldsState(updatedfields)
  }, [
    movement_type_id,
    recipient_customer_id,
    setFormData,
    customerSelect,
    movementTypesSelect,
    itemsForConfirmationModal?.rawItems,
  ])

  useEffect(() => {
    if (itemsForConfirmationModal) {
      setConfirmedItems(itemsForConfirmationModal.notConfirmedItems)
      setConfirmedItemsReplacement(
        itemsForConfirmationModal.notConfirmedItemsReplacement
      )
    }
  }, [itemsForConfirmationModal])

  // --- Mutação para confirmar um carregamento ---
  const confirmShipmentMutation = useMutation({
    mutationFn: async (data) => {
      return await baseServicePost(
        entities.shipments.create + '/confirm-shipment',
        data
      )
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        // * Dispara um protocolo de entrega individual por movimento confirmado.
        const movementIds = response.result?.movement_ids ?? []

        if (movementIds.length > 0) {
          enqueueReports(
            movementIds.map((movementId) => ({
              url: entities.reports.protocol(movementId),
              params: {},
              headers: { 'shipment-id': shipmentId },
            }))
          )
        }

        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['shipments'] })
        // Invalida MovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['movementShipments'] })
        // Invalida MovementsPage
        queryClient.invalidateQueries({ queryKey: ['movements'] })
        // Invalida MovementsDetailsPage
        queryClient.invalidateQueries({ queryKey: ['movementItems'] })
        // Atualiza endereço/status dos equipamentos afetados pela confirmação.
        queryClient.invalidateQueries({ queryKey: ['equipment'] })
        queryClient.invalidateQueries({ queryKey: ['equipments'] })
        // Invalida ValidateMovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['expectedShipmentItems'] })
        // Invalida DetailsMovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['shipmentDetails'] })
        queryClient.invalidateQueries({ queryKey: ['shipmentItemEquipments'] })
        queryClient.invalidateQueries({ queryKey: ['shipmentItemDetails'] })
        queryClient.invalidateQueries({ queryKey: ['shipmentEquipments'] })
        // Invalidar o ValidateShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['shipmentReceivedItems'] })

        // Invalida CreateContractOsPage
        queryClient.invalidateQueries({ queryKey: ['contractDetails'] })
        queryClient.invalidateQueries({ queryKey: ['contractItems'] })

        // Invalida DetailsMovementsShipmentsPage
        queryClient.invalidateQueries({ queryKey: ['shipmentItemVolumes'] })

        navigate(from, { replace: true })
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
  })

  async function handleSubmit() {
    try {
      if (!shipmentId) {
        showNotification(
          'error',
          'Nenhum carregamento selecionado para confirmar.'
        )
        return
      }

      const movementIds = itemsForConfirmationModal?.movementIds ?? []
      if (movementIds.length === 0) {
        showNotification(
          'error',
          'Não há movimentos disponíveis para confirmar.'
        )
        return
      }

      const movementType = Number(formData.shipment.movement_type_id?.value)
      const recipientCustomer = formData.shipment.recipient_customer_id?.value

      const items =
        movementType === MovementEnum.ALUGUEL ||
        movementType === MovementEnum.RESERVA ||
        movementType === MovementEnum.AVULSO
          ? notConfirmedItems
          : confirmedItems

      const itemsDevolution =
        movementType === MovementEnum.DEVOLUCAO
          ? confirmedItems
          : notConfirmedItems

      // Somente para substituição
      const itemsReplacement =
        movementType === MovementEnum.SUBSTITUICAO
          ? notConfirmedItemsReplacement
          : confirmedItemsReplacement

      const payload = {
        shipment_id: Number(shipmentId),
        recipient_customer_id: recipientCustomer,
        movement_type_id: movementType,
        movement_ids: movementIds,

        ...((movementType === MovementEnum.ALUGUEL ||
          movementType === MovementEnum.RESERVA ||
          movementType === MovementEnum.AVULSO) && {
          return_equipments: items
            .filter((item) => item.type === 'equipment')
            .map((item) => item.value),

          return_volumes: items
            .filter((item) => item.type === 'volume')
            .map((item) => item.value),
        }),

        ...(movementType === MovementEnum.DEVOLUCAO && {
          return_equipments: itemsDevolution
            .filter((item) => item.type === 'equipment')
            .map((item) => item.value),
          // 🆕 Base de destino da devolução
          ...(destinationBaseId && {
            equipment_address_overrides: Object.fromEntries(
              itemsDevolution
                .filter((item) => item.type === 'equipment')
                .map((item) => [
                  item.value,
                  { address_id: destinationBaseId, location_id: null },
                ])
            ),
          }),
        }),

        // 🆕 Base onde o equipamento ficará reservado
        ...(movementType === MovementEnum.RESERVA &&
          destinationBaseId && {
            equipment_address_overrides: Object.fromEntries(
              // Em reserva, o destino é aplicado aos equipamentos entregues
              // (coluna confirmedItems), não aos equipamentos marcados como
              // retornados (return_equipments / notConfirmedItems).
              confirmedItems
                .filter((item) => item.type === 'equipment')
                .map((item) => [
                  item.value,
                  { address_id: destinationBaseId, location_id: null },
                ])
            ),
          }),

        ...(movementType === MovementEnum.SUBSTITUICAO && {
          return_equipments: items
            .filter((item) => item.type === 'equipment')
            .map((item) => item.value),

          return_equipments_replacement: itemsReplacement
            .filter((item) => item.type === 'equipment')
            .map((item) => item.value),

          // A base escolhida vale para os equipamentos antigos retirados do cliente.
          ...(destinationBaseId && {
            equipment_address_overrides: Object.fromEntries(
              items
                .filter((item) => item.type === 'equipment')
                .map((item) => [
                  item.value,
                  { address_id: destinationBaseId, location_id: null },
                ])
            ),
          }),
        }),
      }

      await confirmShipmentMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao confirmar carregamento:', {
        status: error?.status,
        message: error?.message,
      })
    }
  }

  // * Desabilitar o campo de "topo do movimento a ser confirmado" enquanto não seleciona o cliente
  useEffect(() => {
    let updatedfields = [...confirmShipmentFields[0].fields]

    if (!formData.shipment.recipient_customer_id?.value) {
      updatedfields = updateProps({
        arrayDependente: updatedfields,
        field_id: 'shipment.movement_type_id',
        updates: { disabled: true },
      })
    } else {
      updatedfields = updateProps({
        arrayDependente: updatedfields,
        field_id: 'shipment.movement_type_id',
        updates: { disabled: false },
      })
    }

    setshipmentFieldsState(updatedfields)
  }, [formData.shipment.recipient_customer_id?.value])

  const getStepsByMovementType = ({
    movementType,
    notConfirmedItems,
    confirmedItems,
    notConfirmedItemsReplacement,
    confirmedItemsReplacement,
    setNotConfirmedItems,
    setConfirmedItems,
    setNotConfirmedItemsReplacement,
    setConfirmedItemsReplacement,
  }) => {
    switch (movementType) {
      case MovementEnum.ALUGUEL:
        return [
          {
            title: 'Aluguel',
            list: {
              label: '',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItems}
                  rightItems={confirmedItems}
                  onLeftItemsChange={setNotConfirmedItems}
                  onRightItemsChange={setConfirmedItems}
                  titleLeft="Retornados"
                  titleRight="Entregues"
                />
              ),
            },
          },
        ]
      case MovementEnum.RESERVA:
        return [
          {
            title: 'Reserva',
            list: {
              label: '',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItems}
                  rightItems={confirmedItems}
                  onLeftItemsChange={setNotConfirmedItems}
                  onRightItemsChange={setConfirmedItems}
                  titleLeft="Retornados"
                  titleRight="Entregues"
                />
              ),
            },
          },
        ]
      case MovementEnum.DEVOLUCAO:
        return [
          {
            title: 'Devolução',
            list: {
              label: '',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItems}
                  rightItems={confirmedItems}
                  onLeftItemsChange={setNotConfirmedItems}
                  onRightItemsChange={setConfirmedItems}
                  titleLeft="Pendentes de Retirada"
                  titleRight="Retirados"
                />
              ),
            },
          },
        ]
      case MovementEnum.SUBSTITUICAO:
        return [
          {
            title: 'Substituição',
            list: {
              label: 'Equipamento Atual (será retirado)',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItems}
                  rightItems={confirmedItems}
                  onLeftItemsChange={setNotConfirmedItems}
                  onRightItemsChange={setConfirmedItems}
                  titleLeft="Pendentes de Retirada"
                  titleRight="Retirados"
                />
              ),
            },
            list2: {
              label: 'Equipamento Novo (será entregue)',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItemsReplacement}
                  rightItems={confirmedItemsReplacement}
                  onLeftItemsChange={setNotConfirmedItemsReplacement}
                  onRightItemsChange={setConfirmedItemsReplacement}
                  titleLeft="Pendentes de Entrega"
                  titleRight="Entregues"
                />
              ),
            },
          },
        ]
      case MovementEnum.AVULSO:
        return [
          {
            title: 'Avulso',
            list: {
              label: '',
              component: (
                <MovementsShipmentsTransferList
                  leftItems={notConfirmedItems}
                  rightItems={confirmedItems}
                  onLeftItemsChange={setNotConfirmedItems}
                  onRightItemsChange={setConfirmedItems}
                  titleLeft="Retornados"
                  titleRight="Entregues"
                />
              ),
            },
          },
        ]
      default:
        return []
    }
  }

  const transferLists = useMemo(() => {
    if (
      !formData.shipment.movement_type_id?.value ||
      !itemsForConfirmationModal ||
      isFetchingShipmentItems
    )
      return []

    return getStepsByMovementType({
      movementType: Number(formData.shipment.movement_type_id?.value),
      notConfirmedItems,
      confirmedItems,
      notConfirmedItemsReplacement,
      confirmedItemsReplacement,
      setNotConfirmedItems,
      setConfirmedItems,
      setNotConfirmedItemsReplacement,
      setConfirmedItemsReplacement,
    })
  }, [
    formData.shipment.movement_type_id?.value,
    itemsForConfirmationModal,
    isFetchingShipmentItems,
    notConfirmedItems,
    confirmedItems,
    notConfirmedItemsReplacement,
    confirmedItemsReplacement,
  ])

  const movementType = Number(formData.shipment.movement_type_id?.value)
  const hasPartialDeliveryConfirmation =
    movementType === MovementEnum.SUBSTITUICAO
      ? confirmedItemsReplacement.some((item) => item.type === 'equipment') &&
        notConfirmedItemsReplacement.some((item) => item.type === 'equipment')
      : [
          MovementEnum.ALUGUEL,
          MovementEnum.RESERVA,
          MovementEnum.AVULSO,
        ].includes(movementType) &&
        confirmedItems.some((item) => item.type === 'equipment') &&
        notConfirmedItems.some((item) => item.type === 'equipment')

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Confirmar Carregamento #${shipmentId}`}
        showBackButton
        backUrl={`/aluguel/carregamentos`}
      />

      <Box
        sx={{
          pr: 1,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: '100%',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Form
            initialFormData={{ ...formData }}
            onSubmit={handleSubmit}
            textSubmit="Confirmar Carregamento"
            textLoadingSubmit="Confirmando..."
            showButtonBack={false}
            showButtons={false}
            isLoading={false}>
            {() => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ShipmentsDetailsSection
                  section={shipmentFieldsState.section}
                  key={shipmentFieldsState.section}>
                  <Box
                    sx={{
                      width: '100%',
                    }}>
                    <SimpleBody
                      fields={shipmentFieldsState}
                      formErrors={normalizeFormErrors(formErrors)}
                      formData={formData}
                      handleFieldChange={handleChange}
                      getOptions={getOptions}
                      getSelectedValue={getSelectedValue}
                      getSelectMeta={getSelectMeta}
                    />
                  </Box>
                </ShipmentsDetailsSection>

                {/* === LISTAS DE TRANSFERÊNCIA === */}
                <EquipmentsList transferLists={transferLists} />

                {hasPartialDeliveryConfirmation && (
                  <Box
                    component="span"
                    role="status"
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 1,
                      color: '#856404',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeeba',
                      borderRadius: 1,
                      fontWeight: 600,
                    }}>
                    Aviso: Esta confirmação será parcial.{' '}
                    {movementType === MovementEnum.ALUGUEL
                      ? 'Os equipamentos recusados serão registrados automaticamente em um movimento de retorno. Depois, gere o protocolo de carregamento desse retorno na página de Carregamentos.'
                      : 'Os itens não confirmados serão tratados conforme o tipo do movimento.'}
                  </Box>
                )}

                {/* Base de destino da devolução, reserva ou equipamento retirado na substituição */}
                {(Number(formData.shipment.movement_type_id?.value) ===
                  MovementEnum.DEVOLUCAO ||
                  Number(formData.shipment.movement_type_id?.value) ===
                    MovementEnum.RESERVA ||
                  Number(formData.shipment.movement_type_id?.value) ===
                    MovementEnum.SUBSTITUICAO) && (
                  <BaseDestinationSelector
                    value={destinationBaseId}
                    onChange={setDestinationBaseId}
                    recipientCustomerId={
                      formData.shipment.recipient_customer_id?.value
                    }
                    mode={
                      Number(formData.shipment.movement_type_id?.value) ===
                      MovementEnum.RESERVA
                        ? 'reserve'
                        : Number(formData.shipment.movement_type_id?.value) ===
                            MovementEnum.SUBSTITUICAO
                          ? 'replacement'
                          : 'devolution'
                    }
                  />
                )}

                {/* BOTÃO  */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    type="submit"
                    text="Confirmar Carregamento"
                    disabled={
                      confirmShipmentMutation.isPending ||
                      isFetchingShipmentItems ||
                      !(itemsForConfirmationModal?.movementIds?.length > 0) ||
                      !formData.shipment.movement_type_id?.value ||
                      !formData.shipment.recipient_customer_id?.value
                    }
                  />
                </Box>
              </Box>
            )}
          </Form>
        </Box>
      </Box>
    </MainLayout>
  )
}

export default ConfirmShipmentsPage
